import { Component, ViewChild, ElementRef, EventEmitter } from '@angular/core';
import { defer as _defer, isEmpty as _isEmpty } from 'lodash';
import { Ng2DeviceService } from 'ng2-device-detector';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { forkJoin as RxForkJoin } from 'rxjs/observable/forkJoin';

import {
  AbstractBlModal,
  ModalOption,
  ModalSize,
  BlFormGroup,
  ModalReason,
  ModalButtonType,
  InputGroupType,
  FaceDetectionInfo,
  BlCameraComponent,
  BlFacingMode,
  BlDialogService,
  BlTextInputComponent,
  BlValidatorService
} from '@blcloud/bl-ng-ui-component';
import { IOrderPartsCartDetails } from '@blcloud/bl-datamodel';
import { IImageViewReference } from '@blcloud/bl-ng-share-module';
import { BlApiErrorInfo } from '@blcloud/bl-ng-common';

import { FaceDetectionModalMode, FaceDetectionRegistState } from './face-detection-modal.enum';
import { IFaceDetectionModalRequest, IFaceDetectionModalResponse } from './face-detection-modal.interface';
import { FaceDetectionModalModeMap } from './face-detection-modal.const';
import { FaceDetectionModalService } from './face-detection-modal.service';
import { IKPASLogin, IKPASRegistMachine, IKPASPerson } from './kpas/kpas.interface';
import { KPASStatus, KPASAppErrStatus } from './kpas/kpas.enum';

/**
 * 顔認証モーダル
 */
@Component({
  selector: 'app-face-detection-modal',
  templateUrl: 'face-detection-modal.component.html',
  styleUrls: ['face-detection-modal.component.scss']
})
export class FaceDetectionModalComponent extends AbstractBlModal {

  /** @ignore Template用 */
  readonly ModalButtonType = ModalButtonType;

  /** @ignore Template用 */
  readonly FaceDetectionModalMode = FaceDetectionModalMode;

  /** @ignore Template用 */
  readonly InputGroupType = InputGroupType;

  /** @ignore Template用 */
  readonly FaceDetectionModalModeMap = FaceDetectionModalModeMap;

  /** @ignore Template用 */
  readonly FaceDetectionRegistState = FaceDetectionRegistState;

  /** @ignore 閉じるボタンView */
  @ViewChild('closeButton') private closeButtonView: ElementRef;

  /** @ignore カメラView */
  @ViewChild('camera') private cameraView: BlCameraComponent;

  /** @ignore ログインIDInputView */
  @ViewChild('loginId') private loginIdInputView: BlTextInputComponent;

  /** @ignore パスワードInputView */
  @ViewChild('password') private passwordInputView: BlTextInputComponent;

  /** 登録ボタンView */
  @ViewChild('registButton') private registButtonView: ElementRef;

  /** @override */
  data: IFaceDetectionModalRequest;

  /** @ignore 動作モード */
  _mode: FaceDetectionModalMode;

  /** @ignore フォールムグループ */
  _blFormGroup = new BlFormGroup();

  /** @ignore 画像登録一時停止 */
  _isPauseRegist = true;

  /** @ignore 登録画像データ参照元 */
  _imageViewRef: IImageViewReference;

  /** @ignore キャプチャ画像表示中 */
  _isShownCaptureImage = false;

  /** @ignore 登録状態 */
  _registState = FaceDetectionRegistState.Init;

  /** @ignore 顔認識の準備完了フラグ */
  _isReadyFaceDetection = false;

  /** @ignore カメラモード */
  _camera;

  /** @ignore .CログインID */
  _loginId: string;

  /** @ignore .Cログインパスワード */
  _password: string;

  /** ログイン情報 */
  private login: IKPASLogin;

  /** ユーザー情報 */
  private person: IKPASPerson;

  /** Camera準備完了Emitter */
  private readyEmiter: EventEmitter<boolean> = new EventEmitter();

  /** 認識情報 */
  private detection: FaceDetectionInfo;

  /** 返却データ */
  private result: IFaceDetectionModalResponse;

  constructor(
    private deviceService: Ng2DeviceService,
    private faceDetectionModalService: FaceDetectionModalService,
    private blDialogService: BlDialogService,
    private blValidatorService: BlValidatorService
  ) {
    super();
  }

  /**
   * @override
   */
  getResults() {
    return this.result;
  }

  /**
   * @override
   */
  defaultOption(_data: IOrderPartsCartDetails) {
    return {
      size: ModalSize.Large,
      fullHeight: true,
    } as ModalOption;
  }

  /**
   * @override
   */
  onShown() {
    this.initialize();
  }

  // ============================================================
  // イベントハンドラ

  /**
   * @ignore
   * 閉じるボタンクリック検知
   */
  onClickClose() {
    this.result = null;
    this.emitDismissEvent(ModalReason.Cancel);
  }

  /**
   * モード変更検知
   * @param mode 顔認証モーダル動作モード
   */
  onChangeMode(mode: FaceDetectionModalMode) {
    this.cameraView.stopFaceDetection();

    this._mode = mode;

    this._isPauseRegist = false;
    this._isShownCaptureImage = false;
    this._imageViewRef = null;
    this.detection = null;
    this._registState = FaceDetectionRegistState.Init;

    _defer(() => {
      // 顔認識を行うカメラ描画領域サイズが異なるので調整が必要
      this.cameraView.detectionSizeAdjust();

      if (this._mode === FaceDetectionModalMode.Auth) {
        this.cameraView.startFaceDetection();
      } else {
        this.loginIdInputView.focus();
      }
    });
  }

  /**
   * 顔認識準備完了検知
   */
  onReadyFaceDetection() {
    this.readyEmiter.next(true);
    this.readyEmiter.complete();
  }

  /**
   * @ignore
   * 顔認識完了検知
   * @param detection 認識情報
   */
  onDoneFaceDetection(detection: FaceDetectionInfo) {
    this.detection = detection;

    this._isShownCaptureImage = true;
    this.cameraView.stopFaceDetection();
    this._imageViewRef = { url: this.detection.imageBuffer };

    switch (this._mode) {
      case FaceDetectionModalMode.Auth:
        this.handleAuthModeFaceDetection();
        break;
      case FaceDetectionModalMode.Regist:
        this.handleRegistModeFaceDetection();
        break;
    }
  }

  /**
   * @ignore
   * リトライボタンクリック検知
   */
  onClickRetry() {
    this._registState = FaceDetectionRegistState.Retry;
    this._isPauseRegist = true;
    this._isShownCaptureImage = false;

    _defer(() => this.cameraView.startFaceDetection());
  }

  /**
   * @ignore
   * 登録ボタンクリック検知
   */
  onClickRegist() {
    this._isPauseRegist = true;

    if (!this.person) {
      this.showLoading();

      this.faceDetectionModalService.registPerson(this.login.token, this._loginId).subscribe({
        next: registPersons => {
          if (registPersons.result) {
            this.person = registPersons.persons[0];
            this.registFaceImage();
          } else {
            // TODO
            // なんかエラー発生したので考慮必要
            alert('KPASクラウドエラー');
            console.log(registPersons);
          }
        },
        error: (error: BlApiErrorInfo) => this.emitError(error)
      });
    } else {
      this.registFaceImage();
    }
  }

  /**
   * 顔画像を登録する
   */
  private registFaceImage() {
    this.showLoading();

    this.faceDetectionModalService.registImage(
      this.login.token,
      this.person.externalId,
      this.detection.imageBuffer
    ).subscribe({
      next: registImage => {
        this.hideLoading();

        if (registImage.result) {
          const mockLogin = this.faceDetectionModalService.backupStorage(this._loginId, this._password, this.person);

          this._registState = FaceDetectionRegistState.Regist;
          this.result = { loginUser: mockLogin };
          this.emitDismissEvent(ModalReason.Done);
        } else {
          // TODO
          // なんかエラー発生したので考慮必要
          alert('KPASクラウドエラー');
          console.log(registImage);
        }
      },
      error: (error: BlApiErrorInfo) => {
        // this.emitError(error);
        alert('サービス提供側とエラー解析中なので、エラー無視して継続します');
        console.warn(error);
        this.hideLoading();

        const mockLogin = this.faceDetectionModalService.backupStorage(this._loginId, this._password, this.person);
        this._registState = FaceDetectionRegistState.Regist;
        this.result = { loginUser: mockLogin };
        this.emitDismissEvent(ModalReason.Done);
      }
    });
  }

  /**
   * @ignore
   * 撮影ボタンクリック検知
   */
  onClickCaptuer() {
    this._isPauseRegist = true;
    this.cameraView.startFaceDetection();
  }

  /**
   * ログインID変更検知
   */
  onChangeLoginId() {
    if (!this._loginId) {
      this.person = null;
      return;
    }

    this.showLoading();
    this.faceDetectionModalService.searchPerson(this.login.token, this._loginId).subscribe({
      next: searchPersons => {
        if (searchPersons.result) {
          if (_isEmpty(searchPersons.persons)) {
            this.hideLoading(this.passwordInputView);
            this.person = null;
          } else {
            if (searchPersons.persons[0].status === KPASStatus.Active) {
              this.hideLoading();

              this.blDialogService.confirm('すでに顔情報が登録されています。\n認証モードに変更しますか？').subscribe(
                dialogRef => {
                  if (dialogRef.reason === ModalReason.Done) {
                    dialogRef.hide(this.closeButtonView.nativeElement);

                    this._loginId = this._password = null;
                    this.onChangeMode(FaceDetectionModalMode.Auth);
                  } else {
                    dialogRef.hide(this.passwordInputView);

                    this.person = searchPersons.persons[0];
                    const mockLogin = this.faceDetectionModalService.getMockLogin(this.person);
                    if (mockLogin) {
                      this._password = mockLogin.password;
                    }
                  }
                }
              );
            } else {
              this.hideLoading(this.passwordInputView);
              this.person = searchPersons.persons[0];
            }
          }
        } else {
          this.hideLoading(this.loginIdInputView);

          // TODO
          // なんかエラー発生したので考慮必要
          // とりあえず値はもとに戻す
          alert('KPASクラウドエラー');
          console.warn(searchPersons);
          this._loginId = null;
        }
      },
      error: (error: BlApiErrorInfo) => this.emitError(error)
    });
  }

  // ============================================================
  // 内部処理

  /**
   * 初期処理
   */
  private initialize() {
    this.showLoading();
    this._camera = this.deviceService.isDesktop() ? { exact: BlFacingMode.Rear } : BlFacingMode.Front;

    RxForkJoin(this.faceDetectionModalService.fetchConfig(), this.readyEmiter.asObservable()).subscribe({
      next: ([[login, rules], isReady]) => {
        this.hideLoading(this.closeButtonView.nativeElement);

        this.login = login;
        this._blFormGroup = this.blValidatorService.getBlFormGroup(rules);
        this._isReadyFaceDetection = isReady;

        this.onChangeMode(FaceDetectionModalMode.Auth);
      },
      error: (error: BlApiErrorInfo) => this.emitError(error)
    });
  }

  /**
   * 顔認識完了検知(登録モード)
   */
  private handleRegistModeFaceDetection() {
    this._registState = FaceDetectionRegistState.Check;
    this._isPauseRegist = false;
    _defer(() => this.registButtonView.nativeElement.focus());
  }

  /**
   * 顔認識完了検知(認証モード)
   */
  private handleAuthModeFaceDetection() {
    this.showLoading('照合中です。\nしばらくお待ち下さい。');
    this.faceDetectionModalService.searchMachines(this.login.token).subscribe({
      next: machines => {
        let machine$: Observable<IKPASRegistMachine>;
        if (machines.count) {
          machine$ = this.faceDetectionModalService.updateMachineToken(this.login.token, machines.machines[0].machineId).pipe(
            map(
              updateToken => {
                return {
                  result: updateToken.result,
                  error: updateToken.error,
                  machineId: machines.machines[0].machineId,
                  machineToken: updateToken.machineToken,
                } as IKPASRegistMachine;
              }
            )
          );
        } else {
          machine$ = this.faceDetectionModalService.registMachine(this.login.token);
        }

        machine$.subscribe({
          next: machine => {
            if (machine.result) {
              this.faceDetectionModalService.faceMatch(machine.machineId, machine.machineToken, this.detection.imageBuffer).subscribe({
                next: faceMatch => {
                  this.hideLoading();

                  if (faceMatch.result) {
                    const storageSaveUser = this.faceDetectionModalService.getMockLogin(faceMatch);
                    if (storageSaveUser && storageSaveUser.loginId) {
                      this.blDialogService.confirm(`顔の特定に成功しました。\nアカウント名「${storageSaveUser.loginId}」にてログインしますか？`).subscribe({
                        next: dialogRef => {
                          dialogRef.hide(this.closeButtonView.nativeElement);

                          if (dialogRef.reason === ModalReason.Done) {
                            this.result = { loginUser: storageSaveUser };
                            this.emitDismissEvent(ModalReason.Done);
                          } else {
                            this._isShownCaptureImage = false;
                            _defer(() => this.cameraView.startFaceDetection());
                          }
                        }
                      });
                    } else {
                      this.blDialogService.warn('LocalStorageとKPAS登録状況が不一致です。\nLocalStorageを確認してください。').subscribe({
                        next: dialogRef => {
                          dialogRef.hide(this.closeButtonView.nativeElement);

                          this._isShownCaptureImage = false;
                          _defer(() => this.cameraView.startFaceDetection());
                        }
                      });
                    }
                  } else {
                    switch (faceMatch.error) {
                      case KPASAppErrStatus.NoPerson:
                      case KPASAppErrStatus.MmultiMatch:
                        this.hideLoading();
                        this.blDialogService.confirm('該当する顔を確認できませんでした。\n再認証しますか？').subscribe(
                          dialogRef => {
                            dialogRef.hide(this.closeButtonView.nativeElement);
                            if (dialogRef.reason === ModalReason.Done) {
                              this._isShownCaptureImage = false;
                              _defer(() => this.cameraView.startFaceDetection());
                            } else {
                              this.onChangeMode(FaceDetectionModalMode.Regist);
                            }
                          }
                        );
                        break;
                      default:
                        // TODO
                        // なんかエラー発生したので考慮必要
                        alert('KPASクラウドエラー');
                        console.log(faceMatch);
                        break;
                    }
                  }
                },
                error: (faceMatchError: BlApiErrorInfo) => this.emitError(faceMatchError)
              });
            } else {
              // TODO
              // なんかエラー発生したので考慮必要
              alert('KPASクラウドエラー');
              console.log(machine);
            }
          },
          error: (machinePUError: BlApiErrorInfo) => this.emitError(machinePUError)
        });
      },
      error: (searchMachineError: BlApiErrorInfo) => this.emitError(searchMachineError)
    });
  }

  /**
   * エラー終了を通知する
   */
  private emitError(error: BlApiErrorInfo) {
    this.result = { error };
    this.emitDismissEvent(ModalReason.Error);
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/face-detection-modal/face-detection-modal.component.ts